import { Component } from '@theme/component';
import { preloadImage } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Full-screen popup gallery dialog with 3-column layout:
 * thumbnails (left) | main image (center) | info panel (right)
 * plus a fixed buy bar at the bottom.
 *
 * Implements the same interface as ZoomDialog so MediaGallery can
 * use it via ref="zoomDialogComponent".
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media - slide wrappers
 * @property {HTMLElement} thumbnails - thumbnail container
 *
 * @extends Component<Refs>
 */
export class PopupGalleryDialog extends Component {
  requiredRefs = ['dialog'];

  /** @type {number} */
  #currentIndex = 0;

  /** @type {Set<string>} */
  #highResLoaded = new Set();

  /** @type {boolean} */
  #isMobile = false;

  /** @type {MutationObserver | null} */
  #preorderObserver = null;

  /** @type {HTMLButtonElement | null} */
  #preorderMirrorButton = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pg-swatch-change', this.#handleSwatchChange);
    this.#checkMobile();
    this.#bindZoomListeners();
    this.#bindCustomSelects();
    this.#bindGalleryThumbnails();
    this.#setupPreorderMirror();
    document.addEventListener('click', this.#onDocumentClick);
    this.refs.dialog.addEventListener('click', this.#onDialogClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pg-swatch-change', this.#handleSwatchChange);
    this.#preorderObserver?.disconnect();
    this.#preorderObserver = null;
    document.removeEventListener('click', this.#onDocumentClick);
    this.refs.dialog.removeEventListener('click', this.#onDialogClick);
  }

  /**
   * Closes the gallery when clicking the backdrop (outside the dialog box).
   * With showModal(), a click on the ::backdrop has the dialog element itself
   * as its target, so content clicks (which target inner elements) don't close.
   * @param {MouseEvent} event
   */
  #onDialogClick = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };

  #checkMobile() {
    this.#isMobile = window.matchMedia('(max-width: 749px)').matches;
  }

  #bindZoomListeners() {
    const slides = this.querySelectorAll('.popup-gallery__slide[data-zoomable]');
    for (const slide of slides) {
      slide.addEventListener('mouseenter', this.#onSlideMouseEnter);
      slide.addEventListener('mousemove', this.#onSlideMouseMove);
      slide.addEventListener('mouseleave', this.#onSlideMouseLeave);
    }
  }

  /**
   * Bind click handlers to the underlying media-gallery's slideshow-control
   * thumbnails so clicking one opens the popup gallery at the matching slide.
   * The slideshow's own /select action still fires, keeping the inline
   * gallery in sync with the popup.
   */
  #bindGalleryThumbnails() {
    const gallery = this.closest('media-gallery');
    if (!gallery) return;
    const thumbs = gallery.querySelectorAll('.slideshow-controls__thumbnail');
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', (event) => {
        // Video thumbnails route to <video-popup> via on:click="#video-popup-…/open/N";
        // skip opening the gallery popup so only the video popup shows.
        const onClick = thumb.getAttribute('on:click') || '';
        if (onClick.includes('#video-popup-')) return;
        // Pull the slide index from the existing on:click="/select/N" binding so
        // duplicated control groups (desktop + mobile) stay aligned.
        const match = onClick.match(/\/select\/(\d+)/);
        const index = match ? parseInt(match[1], 10) : 0;
        this.open(index, event);
      });
    });
  }

  /* ── Public API (matches ZoomDialog interface) ── */

  /**
   * Opens the popup gallery at the given slide index.
   * @param {number} index
   * @param {Event} [event]
   */
  open(index, event) {
    this.#checkMobile();
    // On mobile, skip the popup gallery entirely so the click falls through to
    // default Shopify slideshow behavior (swipe / inline navigation).
    if (this.#isMobile) return;
    event?.preventDefault();
    this.refs.dialog.showModal();
    this.#showSlide(index);
  }

  /** Closes the popup gallery dialog. */
  close() {
    this.refs.dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Preloads a high-resolution image for a media container.
   * @param {HTMLElement} container
   */
  loadHighResolutionImage(container) {
    if (!container) return;
    const img = container.querySelector('img.product-media__image, img');
    if (!(img instanceof HTMLImageElement)) return;

    const highRes = img.getAttribute('data_max_resolution');
    if (!highRes || this.#highResLoaded.has(highRes)) return;

    preloadImage(highRes);
    const probe = new Image();
    probe.onload = () => {
      img.src = highRes;
      this.#highResLoaded.add(highRes);
    };
    probe.src = highRes;
  }

  /* ── Navigation ── */

  prev() {
    const slides = this.#slides;
    if (!slides.length) return;
    const i = this.#currentIndex > 0 ? this.#currentIndex - 1 : slides.length - 1;
    this.#showSlide(i);
  }

  next() {
    const slides = this.#slides;
    if (!slides.length) return;
    const i = this.#currentIndex < slides.length - 1 ? this.#currentIndex + 1 : 0;
    this.#showSlide(i);
  }

  /**
   * Handles thumbnail click via declarative on:click binding.
   * @param {number} index
   */
  handleThumbnailClick(index) {
    this.#showSlide(index);
  }

  /**
   * Keyboard handler for the dialog.
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.prev();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.next();
        break;
    }
  }

  /* ── Zoom on hover ── */

  /** @param {MouseEvent} event */
  #onSlideMouseEnter = (event) => {
    if (this.#isMobile) return;

    const slide = /** @type {HTMLElement} */ (event.currentTarget);
    if (!slide.classList.contains('popup-gallery__slide--active')) return;

    slide.classList.add('popup-gallery__slide--zooming');
    this.#updateZoomOrigin(slide, event);
  };

  /** @param {MouseEvent} event */
  #onSlideMouseMove = (event) => {
    if (this.#isMobile) return;

    const slide = /** @type {HTMLElement} */ (event.currentTarget);
    if (!slide.classList.contains('popup-gallery__slide--zooming')) return;

    this.#updateZoomOrigin(slide, event);
  };

  /** @param {MouseEvent} event */
  #onSlideMouseLeave = (event) => {
    const slide = /** @type {HTMLElement} */ (event.currentTarget);
    this.#exitZoom(slide);
  };

  /**
   * @param {HTMLElement} slide
   */
  #exitZoom(slide) {
    slide.classList.remove('popup-gallery__slide--zooming');
    const img = slide.querySelector('img');
    if (img) img.style.transformOrigin = '';
  }

  /**
   * @param {HTMLElement} slide
   * @param {MouseEvent} event
   */
  #updateZoomOrigin(slide, event) {
    const img = slide.querySelector('img');
    if (!img) return;

    const rect = slide.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    img.style.transformOrigin = `${x}% ${y}%`;
  }

  /* ── Buy bar: custom-select dropdowns (color + size) ── */

  #bindCustomSelects() {
    const selects = this.querySelectorAll('[data-popup-select]');
    selects.forEach((select) => {
      const toggle = select.querySelector('[data-popup-select-toggle]');
      const list = select.querySelector('.popup-gallery__select-list');
      const options = select.querySelectorAll('.popup-gallery__select-option');
      if (!toggle || !list) return;

      toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        this.#toggleSelect(select);
      });

      options.forEach((option) => {
        option.addEventListener('click', (event) => {
          event.stopPropagation();
          this.#selectOption(select, /** @type {HTMLElement} */ (option));
        });
      });
    });
  }

  /**
   * Close all popup-gallery selects when clicking outside.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const target = /** @type {Node} */ (event.target);
    const selects = this.querySelectorAll('[data-popup-select]');
    selects.forEach((select) => {
      if (!select.contains(target)) this.#closeSelect(select);
    });
  };

  /** @param {Element} select */
  #toggleSelect(select) {
    const isOpen = select.classList.contains('popup-gallery__custom-select--open');
    // Close siblings first
    this.querySelectorAll('[data-popup-select]').forEach((s) => {
      if (s !== select) this.#closeSelect(s);
    });
    if (isOpen) this.#closeSelect(select);
    else this.#openSelect(select);
  }

  /** @param {Element} select */
  #openSelect(select) {
    select.classList.add('popup-gallery__custom-select--open');
    const toggle = select.querySelector('[data-popup-select-toggle]');
    const list = select.querySelector('.popup-gallery__select-list');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    if (list) list.removeAttribute('hidden');
  }

  /** @param {Element} select */
  #closeSelect(select) {
    select.classList.remove('popup-gallery__custom-select--open');
    const toggle = select.querySelector('[data-popup-select-toggle]');
    const list = select.querySelector('.popup-gallery__select-list');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    if (list) list.setAttribute('hidden', '');
  }

  /**
   * Handle option selection.
   * - Color: update display + trigger the matching info-panel swatch
   *   (which fires `pg-swatch-change`, syncing image + variant id).
   * - Size:  update display + recompute variant id from color+size.
   * @param {Element} select
   * @param {HTMLElement} option
   */
  #selectOption(select, option) {
    const type = select.getAttribute('data-popup-select');
    const value = option.getAttribute('data-value') || '';

    // Update selected state
    select.querySelectorAll('.popup-gallery__select-option').forEach((o) => {
      o.classList.remove('popup-gallery__select-option--selected');
      o.setAttribute('aria-selected', 'false');
    });
    option.classList.add('popup-gallery__select-option--selected');
    option.setAttribute('aria-selected', 'true');

    if (type === 'color') {
      // Update toggle display
      const nameEl = select.querySelector('[data-popup-color-name]');
      const imgEl = select.querySelector('[data-popup-color-img]');
      const imgWrap = select.querySelector('[data-popup-color-img-wrap]');
      const colorImage = option.getAttribute('data-color-image') || '';
      if (nameEl) nameEl.textContent = value;
      if (imgEl instanceof HTMLImageElement && colorImage) {
        imgEl.src = colorImage;
        imgEl.alt = value;
      }
      if (imgWrap) {
        if (colorImage) imgWrap.removeAttribute('hidden');
        else imgWrap.setAttribute('hidden', '');
      }

      // Trigger the matching swatch in the info panel so the rest of the
      // popup (main image, detail card, example products) stays in sync.
      const panelSwatch = /** @type {HTMLElement|null} */ (
        this.querySelector(`.pg-info-panel__swatch[data-option-value="${CSS.escape(value)}"]`)
      );
      if (panelSwatch) panelSwatch.click();
    } else if (type === 'size') {
      const nameEl = select.querySelector('[data-popup-size-name]');
      if (nameEl) nameEl.textContent = value;
      this.#updateVariantFromOptions();
    }

    this.#closeSelect(select);
  }

  /**
   * Resolve current variant id from the displayed color + size in the buy bar
   * and write it to `data-current-variant-id` (used by addToCart).
   */
  #updateVariantFromOptions() {
    const colorEl = this.querySelector('[data-popup-color-name]');
    const sizeEl = this.querySelector('[data-popup-size-name]');
    const color = colorEl?.textContent?.trim() || '';
    const size = sizeEl?.textContent?.trim() || '';
    if (!color && !size) return;

    // Try to find a variant-id via the matching info-panel swatch when only color is chosen.
    if (color && !size) {
      const panelSwatch = this.querySelector(
        `.pg-info-panel__swatch[data-option-value="${CSS.escape(color)}"]`
      );
      const vid = panelSwatch?.getAttribute('data-variant-id');
      if (vid) this.dataset.currentVariantId = vid;
      return;
    }

    // For size-only selects (no color option), look up via the selected size option
    // matching the active color's swatch — fallback to the first option carrying data-variant-id.
    const selectedSizeOpt = this.querySelector(
      `[data-popup-select="size"] .popup-gallery__select-option--selected`
    );
    const vid = selectedSizeOpt?.getAttribute('data-variant-id');
    if (vid) this.dataset.currentVariantId = vid;
  }

  /* ── Buy bar: quantity stepper ── */

  decrementQty() {
    const input = this.querySelector('[data-popup-qty]');
    if (!(input instanceof HTMLInputElement)) return;
    const val = parseInt(input.value, 10) || 1;
    input.value = String(Math.max(1, val - 1));
  }

  incrementQty() {
    const input = this.querySelector('[data-popup-qty]');
    if (!(input instanceof HTMLInputElement)) return;
    const val = parseInt(input.value, 10) || 1;
    input.value = String(val + 1);
  }

  /** Add to cart via Cart AJAX API. */
  async addToCart() {
    const variantId = this.dataset.currentVariantId;
    const input = this.querySelector('[data-popup-qty]');
    const qty = parseInt(input?.value, 10) || 1;
    const btn = this.querySelector('.popup-gallery__add-to-cart');

    if (!variantId) return;

    btn?.setAttribute('disabled', '');
    btn && (btn.textContent = 'Adding...');

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty }] }),
      });

      if (!res.ok) throw new Error(res.statusText);

      btn && (btn.textContent = 'Added!');
      setTimeout(() => {
        btn && (btn.textContent = 'add to cart');
        btn?.removeAttribute('disabled');
      }, 1500);

      // Trigger cart drawer refresh
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch {
      btn && (btn.textContent = 'add to cart');
      btn?.removeAttribute('disabled');
    }
  }

  /* ── Private helpers ── */

  /**
   * Mirrors the Restock Rocket pre-order button into the popup gallery buy bar.
   * Restock Rocket auto-injects `.restock-rocket-preorder-button` into the main
   * `.product-form-buttons` container. We watch that container, clone the button
   * into `.popup-gallery__actions`, and route clicks through to the original —
   * same puppet pattern used for the sticky add-to-cart bar.
   */
  #setupPreorderMirror() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buttonsContainer = productForm.querySelector('.product-form-buttons') ?? productForm;

    this.#preorderObserver?.disconnect();
    this.#preorderObserver = new MutationObserver(() => this.#syncPreorderButton());
    this.#preorderObserver.observe(buttonsContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'class', 'style'],
    });

    this.#syncPreorderButton();
  }

  /**
   * Reflects the current state of the main pre-order button into the popup buy bar.
   * Hides the popup ATC button when pre-order is active so only one CTA shows
   * at a time — mirrors the main PDP behavior.
   */
  #syncPreorderButton() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const actionsEl = this.querySelector('.popup-gallery__actions');
    const popupAtcBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.popup-gallery__add-to-cart')
    );
    if (!actionsEl) return;

    const mainPreorder = /** @type {HTMLButtonElement | null} */ (
      productForm.querySelector('.restock-rocket-preorder-button')
    );

    if (mainPreorder && this.#isElementVisible(mainPreorder)) {
      const label = mainPreorder.textContent?.trim() || 'pre-order';

      if (!this.#preorderMirrorButton || !actionsEl.contains(this.#preorderMirrorButton)) {
        const mirror = document.createElement('button');
        mirror.type = 'button';
        mirror.className = 'restock-rocket-preorder-button popup-gallery__preorder';
        mirror.textContent = label;
        mirror.addEventListener('click', this.#handlePreorderClick);
        actionsEl.appendChild(mirror);
        this.#preorderMirrorButton = mirror;
      } else if (this.#preorderMirrorButton.textContent !== label) {
        this.#preorderMirrorButton.textContent = label;
      }

      this.#preorderMirrorButton.disabled = mainPreorder.disabled;
      if (popupAtcBtn) popupAtcBtn.style.display = 'none';
    } else {
      if (this.#preorderMirrorButton) {
        this.#preorderMirrorButton.remove();
        this.#preorderMirrorButton = null;
      }
      if (popupAtcBtn) popupAtcBtn.style.display = '';
    }
  }

  /**
   * Click handler for the mirrored pre-order button — puppet-clicks the main one.
   * @param {MouseEvent} event
   */
  #handlePreorderClick = (event) => {
    event.preventDefault();
    const productForm = this.#getProductForm();
    if (!productForm) return;
    const mainPreorder = /** @type {HTMLButtonElement | null} */ (
      productForm.querySelector('.restock-rocket-preorder-button')
    );
    mainPreorder?.click();
  };

  /**
   * Checks whether an element is rendered (not display:none/hidden).
   * @param {HTMLElement} el
   * @returns {boolean}
   */
  #isElementVisible(el) {
    if (!el || el.hidden) return false;
    const style = getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  /**
   * Locates the main product form for this popup gallery's product/section.
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;
    return sectionElement.querySelector('product-form-component');
  }

  /** @returns {HTMLElement[]} */
  get #slides() {
    return Array.from(this.querySelectorAll('.popup-gallery__slide'));
  }

  /** @returns {HTMLElement[]} */
  get #thumbs() {
    return Array.from(this.querySelectorAll('.popup-gallery__thumb'));
  }

  /**
   * Shows the slide at the given index, updates thumbnails and active state.
   * @param {number} index
   */
  #showSlide(index) {
    const slides = this.#slides;
    const thumbs = this.#thumbs;

    slides.forEach((s, i) => {
      const wasActive = s.classList.contains('popup-gallery__slide--active');
      s.classList.toggle('popup-gallery__slide--active', i === index);
      // Clear zoom when switching slides
      if (wasActive && i !== index) {
        s.classList.remove('popup-gallery__slide--zooming');
        const img = s.querySelector('img');
        if (img) img.style.transformOrigin = '';
        // Pause any playing video in the outgoing slide so YouTube/Vimeo/HTML5 stops in the background
        const deferred = /** @type {any} */ (s.querySelector('deferred-media'));
        if (deferred && typeof deferred.pauseMedia === 'function') {
          deferred.pauseMedia();
        }
      }
    });
    thumbs.forEach((t, i) => {
      t.classList.toggle('popup-gallery__thumb--active', i === index);
      t.setAttribute('aria-selected', String(i === index));
    });

    // Scroll active thumbnail into view
    const activeThumb = thumbs[index];
    activeThumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    this.#currentIndex = index;

    // Load high-res for the active slide
    const activeSlide = slides[index];
    if (activeSlide) this.loadHighResolutionImage(activeSlide);

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * When a swatch is changed in the info panel, switch to the
   * matching variant image slide.
   */
  #handleSwatchChange = (/** @type {CustomEvent} */ event) => {
    const { variantImage } = event.detail || {};
    if (!variantImage) return;

    // Update buy bar variant ID
    const variantId = event.detail.variantId;
    if (variantId) {
      this.dataset.currentVariantId = variantId;
    }

    // Update buy bar color display
    const colorNameEl = this.querySelector('[data-popup-color-name]');
    const colorImgEl = this.querySelector('[data-popup-color-img]');
    if (colorNameEl) colorNameEl.textContent = event.detail.swatchName || '';
    if (colorImgEl instanceof HTMLImageElement) {
      const swatchBtn = this.querySelector('.pg-info-panel__swatch--active');
      const colorImg = swatchBtn?.getAttribute('data-color-image') || '';
      if (colorImg) colorImgEl.src = colorImg;
    }

    // Find matching slide by comparing variant image URL stems
    const stem = variantImage.split('?')[0];
    const slides = this.#slides;
    for (let i = 0; i < slides.length; i++) {
      const img = slides[i].querySelector('img');
      if (img && img.src.split('?')[0] === stem) {
        this.#showSlide(i);
        return;
      }
    }

    // Fallback: check data-variant-image attribute
    for (let i = 0; i < slides.length; i++) {
      const vi = slides[i].getAttribute('data-variant-image');
      if (vi && vi.split('?')[0] === stem) {
        this.#showSlide(i);
        return;
      }
    }
  };
}

if (!customElements.get('popup-gallery')) {
  customElements.define('popup-gallery', PopupGalleryDialog);
}
